import React, { useState, useEffect } from "react";
import { Box, Typography, Slider, TextField } from "@mui/material";
import { motion } from "framer-motion";
import confetti from "canvas-confetti";
import { useInView } from "react-intersection-observer";

const EarningsCalculator = () => {
  const defaultHours = 20;
  const defaultRate = 34;

  const [hours, setHours] = useState(0);
  const [rate, setRate] = useState(0);

  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.3 });

  // Animate hours and rate when in view
  useEffect(() => {
    if (inView) {
      let h = 0;
      let r = 0;

      const hoursInterval = setInterval(() => {
        h += 1;
        setHours(h);
        if (h >= defaultHours) clearInterval(hoursInterval);
      }, 40);

      const rateInterval = setInterval(() => {
        r += 1;
        setRate(r);
        if (r >= defaultRate) clearInterval(rateInterval);
      }, 40);
    }
  }, [inView]);

  useEffect(() => {
    if (hours * rate * 4 > 3500) {
      const isMobile = window.innerWidth <= 768;
      confetti({
        particleCount: isMobile ? 25 : 50,
        spread: isMobile ? 60 : 80,
        origin: { y: 0.6 },
        colors: ["#e63946", "#2c3e50", "#ffffff"],
      });
    }
  }, [hours, rate]);

  const calculateEarnings = () => hours * rate * 4;

  const earningsVariants = {
    update: { scale: 1.1, transition: { duration: 0.2 } },
  };

  return (
    <Box
      className="earnings-calculator-section"
      ref={ref}
      sx={{ py: { xs: 4, md: 8 }, bgcolor: "background.default" }}
    >
      <Typography
        variant="h2"
        color="text.primary"
        align="center"
        gutterBottom
        sx={{ fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" } }}
      >
        Estimate Your Earnings
      </Typography>
      <Typography
        variant="body1"
        color="text.secondary"
        align="center"
        sx={{
          mb: { xs: 3, md: 4 },
          maxWidth: "600px",
          mx: "auto",
          fontSize: { xs: "0.9rem", sm: "1rem" },
        }}
      >
        See how much you could earn by listing your kitchen on AirKitchenz.
      </Typography>
      <Box
        container
        spacing={{ xs: 2, md: 4 }}
        justifyContent="center"
        alignItems="center"
        sx={{ maxWidth: "800px", mx: "auto" }}
      >
        <Typography variant="body1" color="text.primary" gutterBottom>
          Hours per Week
        </Typography>
        <Slider
          value={hours}
          onChange={(e, newValue) => setHours(newValue)}
          min={0}
          max={40}
          step={1}
          valueLabelDisplay="auto"
          color="primary"
          sx={{
            height: 8,
            "& .MuiSlider-thumb": { width: 24, height: 24 },
            mt: 1,
          }}
        />
        <TextField
          type="number"
          value={hours}
          onChange={(e) => setHours(Number(e.target.value))}
          variant="outlined"
          size="small"
          sx={{ mt: 1, width: { xs: "100px", sm: "120px" } }}
        />
        <Typography variant="body1" color="text.primary" gutterBottom>
          Hourly Rate ($)
        </Typography>
        <Slider
          value={rate}
          onChange={(e, newValue) => setRate(newValue)}
          min={10}
          max={100}
          step={1}
          valueLabelDisplay="auto"
          color="primary"
          sx={{
            height: 8,
            "& .MuiSlider-thumb": { width: 24, height: 24 },
            mt: 1,
          }}
        />
        <TextField
          type="number"
          value={rate}
          onChange={(e) => setRate(Number(e.target.value))}
          variant="outlined"
          size="small"
          sx={{ mt: 1, width: { xs: "100px", sm: "120px" } }}
        />
      </Box>
      <Box sx={{ mt: { xs: 3, md: 4 }, textAlign: "center" }}>
        <motion.div
          key={calculateEarnings()}
          initial="update"
          animate="update"
          variants={earningsVariants}
        >
          <Typography
            variant="h4"
            color="primary.main"
            sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem", md: "2rem" } }}
          >
            Rent your kitchen and earn up to $
            {calculateEarnings().toLocaleString()}/month!
          </Typography>
        </motion.div>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 1, fontSize: { xs: "0.8rem", sm: "0.9rem" } }}
        >
          Based on {hours} hours/week at ${rate}/hour.
        </Typography>
      </Box>
    </Box>
  );
};

export default EarningsCalculator;
